import React from "react"
import { Typography, Select, MenuItem, makeStyles, Theme } from "@material-ui/core"
import { Board } from "../../boards/entities/board"
import { useOnMoveCard } from "../../boards/hooks/use-on-move-card"
import { BoardViewModelV2 } from "../../boards/api/v2/board-view-model"
import { useTranslations } from "../hooks/use-translations"
import { getTranslatedDefaultColumnNames } from "../utils"

type Props = {
  board: BoardViewModelV2
  cardId: string
  hideTitle?: boolean
}

const MoveToColumnWidget = ({ board, cardId, hideTitle }: Props) => {
  const boardInstance = React.useMemo(() => new Board(board), [board])
  const { onMoveCard } = useOnMoveCard()
  const translations = useTranslations()
  const classes = useStyles()
  const currentColumnIndex = boardInstance.getCardById(cardId)?.column as number
  const currentColumnId = boardInstance.getColumnByPosition(currentColumnIndex)?.id
  const visibleColumns = boardInstance.getVisibleColumns()

  if (!currentColumnId || visibleColumns.length <= 1) return null

  const handleChange = (destColumnId: string) => {
    const destColumn = boardInstance.getColumnById(destColumnId)
    if (!destColumn || !destColumn.isVisible) return

    onMoveCard({
      board,
      position: destColumn.getNextCardPosition(),
      boardId: board.id,
      cardId,
      columnId: currentColumnId,
      destColumnId,
    })
  }

  return (
    <div>
      {!hideTitle && (
        <Typography variant="body1" component="h6" className={classes.title}>
          <strong>{translations.moveToColumn}</strong>
        </Typography>
      )}
      <Select
        value={currentColumnId}
        className={hideTitle ? classes.select : undefined}
        fullWidth
        variant="outlined"
        onChange={(e) => handleChange(e.target.value as string)}
      >
        {visibleColumns.map((col) => {
          const translatedName = getTranslatedDefaultColumnNames({ columnName: col.defaultName, translations })
          const columnName = col.hasModifiedName ? col.getName() : translatedName

          return (
            <MenuItem key={col.id} value={col.id}>
              {columnName}
            </MenuItem>
          )
        })}
      </Select>
    </div>
  )
}

const useStyles = makeStyles((theme: Theme) => ({
  select: {
    marginTop: theme.spacing(1),
    fontSize: "0.875rem",
    "& .MuiSelect-root": {
      paddingTop: 8,
      paddingBottom: 8,
    },
  },
  title: {
    marginBottom: theme.spacing(1),
  },
}))

export default MoveToColumnWidget
